/**
 * Ambient audio (R35) — the audio layer's content contract (D115).
 *
 * @remarks
 * Audio is the FOURTH adopter of the pattern this engine has proven three
 * times — conditional text, conditional plates, sprite `visibleWhen` — and it
 * deliberately invents no fifth shape. A pack declares a table of audio
 * assets and one pack-level `ambience` (fallback + variants); a room may
 * override the whole resolution with its own `ambience`. The model is Mike's
 * default-plus-override (Calder's 2026-08-28 §6.1): the pack's declaration is
 * the building's sound, and a room speaks up only when something about the
 * world is different there.
 *
 * **Silence is an authored value, never the absence of a field** (§6.2, the
 * load-bearing rule): `'silence'` in an ambience slot means "this is quiet on
 * purpose", while an ABSENT room override means "inherit the pack". The
 * format can therefore distinguish a finished quiet room from a room nobody
 * has gotten to — the same move `framePosition: 'absent'` and `depicts: []`
 * made, and it has paid every time.
 *
 * `playback` is kind-discriminated from day one (§8.2) — the discriminant IS
 * the contract, exactly as it was for sprite placement (D96/D111) when that
 * union also had one member. D264 added the second kind, `cue`: a piece that
 * plays ONCE, when a narrative line naming it is shown (`cues` on the line —
 * see {@link audioCuesSchema}). A positioned `scene` emitter for the
 * Unity-class renderer is still to come, and the split is the sprite split:
 * what/when/whether are portable across every renderer; gain curves,
 * crossfade times, ducking and 3D positioning are renderer-local and never
 * authored here.
 *
 * Ambience is declared on the PACK and on ROOMS, never inside a scene (§8.1):
 * audio is its own presentation axis, not a rung of the visual ladder, and a
 * text-plus-sound game with no pictures at all must be authorable.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { audioIdSchema } from './identifiers.js';
import { nonBlankStringSchema } from './primitives.js';

/**
 * D264: what a cue IS, which is content rather than a knob — music, which a
 * renderer lays the bed down under, or a sound, which plays over it. How far
 * the bed goes down, and how fast, is the renderer's.
 */
export const audioCueLayerSchema = z.enum(['music', 'sound']);

/** A cue's layer — see {@link audioCueLayerSchema}. */
export type AudioCueLayer = z.infer<typeof audioCueLayerSchema>;

/**
 * How a renderer should play one audio asset — kind-discriminated (see the
 * module remarks). `stereoBed` is a seamless loop that holds a place,
 * everywhere at once; `cue` plays once, from its start, when a line that
 * names it is shown. Neither carries renderer knobs: those are the
 * renderer's own.
 */
export const audioPlaybackSchema = z.discriminatedUnion('kind', [
	z.strictObject({
		kind: z.literal('cue'),
		layer: audioCueLayerSchema,
	}),
	z.strictObject({
		kind: z.literal('stereoBed'),
	}),
]);

/** How one audio asset plays — see {@link audioPlaybackSchema}. */
export type AudioPlayback = z.infer<typeof audioPlaybackSchema>;

/**
 * One authored audio asset.
 *
 * @remarks
 * `src` is a pack-relative asset path, exactly as a plate's is. The format at
 * that path is delivery detail (the reference player ships Opus with an AAC
 * sibling); the declaration does not care.
 */
export const audioDefinitionSchema = z
	.strictObject({
		audioId: audioIdSchema,
		playback: audioPlaybackSchema,
		src: nonBlankStringSchema,
	})
	.readonly();

/** One authored audio asset — see {@link audioDefinitionSchema}. */
export type AudioDefinition = z.infer<typeof audioDefinitionSchema>;

/**
 * D264: the sounds a narrative line makes — ids into the audio table, each a
 * `cue`, in the order they play. A cue rides the line that says its sound
 * (the picture-and-prose rule, sound-side: no sound without a line that says
 * it), so every sound already has its caption and a player who plays muted
 * loses nothing the story needs. Three at most: a line that needs more is
 * several lines.
 */
export const audioCuesSchema = z.array(audioIdSchema).min(1).max(3).readonly();

/**
 * One ambience slot's value: an asset, or silence ON PURPOSE. See the module
 * remarks — `'silence'` is a decision the validator can check and a builder
 * can show, never a hole.
 */
export const ambienceValueSchema = z.union([audioIdSchema, z.literal('silence')]);

/** An ambience slot's value — see {@link ambienceValueSchema}. */
export type AmbienceValue = z.infer<typeof ambienceValueSchema>;

/**
 * One conditional ambience choice: this value, whenever this condition holds.
 * The condition is `conditionSchema` VERBATIM — the D96 rule, third
 * application: selection vocabulary is never re-invented per layer.
 */
export const ambienceVariantSchema = z
	.strictObject({
		audio: ambienceValueSchema,
		condition: conditionSchema,
	})
	.readonly();

/** One conditional ambience choice — see {@link ambienceVariantSchema}. */
export type AmbienceVariant = z.infer<typeof ambienceVariantSchema>;

/**
 * An ambience declaration — the `conditionalTextSchema` shape carrying an
 * ambience value (the fourth deliberate mirror; the payload key is `audio`
 * for the same author-clarity reason images carry `imageId`). The plain arm
 * is the common case; `variants` is ordered, first match wins, `min(1)`
 * because an empty object form is a plain value with extra steps.
 *
 * A ROOM carrying one of these overrides the pack's WHOLESALE — its own
 * fallback, its own variants. A room carrying none inherits the pack's. The
 * override is wholesale rather than merged because a half-merged sound
 * design is impossible to debug by listening, and listening is the only debugger
 * this layer has.
 */
export const conditionalAmbienceSchema = z.union([
	ambienceValueSchema,
	z
		.strictObject({
			fallback: ambienceValueSchema,
			variants: z.array(ambienceVariantSchema).min(1).readonly(),
		})
		.readonly(),
]);

/** A possibly-conditional ambience declaration — see {@link conditionalAmbienceSchema}. */
export type ConditionalAmbience = z.infer<typeof conditionalAmbienceSchema>;
